import { useEffect, useRef, type MutableRefObject } from "react";
import { getFacebookConversationsWithMeta } from "@api/services/facebook";
import { CHANNELS } from "@common";
import type { Conversation } from "@interfaces/chat.interface";
import { useChatStore } from "@stores/chatStore";
import { ConversationTabHelpers, type ConversationTabType } from "@constants/conversationTabs";

type Page = { id: string; name: string };

type Params = {
  storeFolderStatusMap: Record<string, string>;
  selectedFanpageName?: string;
  facebookPages: Page[];
  debouncedSearchValue: string;
  activeTab: ConversationTabType;
  selectedTagIds: string[];
  setConversationsWithSearch: (conversations: Conversation[], searchTerm: string) => void;
  isResettingPageRef: MutableRefObject<boolean>;
  isFetchingFromFolderStatusChangeRef: MutableRefObject<boolean>;
  updatePagination: (pageId: string, pagination: { offset: number; total: number; limit: number; hasMore: boolean }) => void;
};

export const useConversationFolderStatusRefresh = ({
  storeFolderStatusMap,
  selectedFanpageName,
  facebookPages,
  debouncedSearchValue,
  activeTab,
  selectedTagIds,
  setConversationsWithSearch,
  isResettingPageRef,
  isFetchingFromFolderStatusChangeRef,
  updatePagination,
}: Params) => {
  const prevFolderStatusMapRef = useRef<string>("");
  const refreshTrigger = useChatStore((state) => state.refreshConversationHistoryTrigger);
  const prevRefreshTriggerRef = useRef<number>(0);

  useEffect(() => {
    const currentFolderStatusMapKey = JSON.stringify(storeFolderStatusMap);

    // Skip if currently fetching or resetting
    if (isFetchingFromFolderStatusChangeRef.current || isResettingPageRef.current) {
      prevFolderStatusMapRef.current = currentFolderStatusMapKey;
      return;
    }

    // On first run, just set the key
    if (prevFolderStatusMapRef.current === "") {
      prevFolderStatusMapRef.current = currentFolderStatusMapKey;
      return;
    }

    const hasFolderStatusChange = prevFolderStatusMapRef.current !== currentFolderStatusMapKey;

    if (!hasFolderStatusChange) {
      prevFolderStatusMapRef.current = currentFolderStatusMapKey;
      return;
    }

    // Check whether new conversations are already in store (from socket)
    const { conversations: currentConversations } = useChatStore.getState();
    const prevFolderStatusMap = JSON.parse(prevFolderStatusMapRef.current || "{}");
    const currentFolderStatusMap = storeFolderStatusMap;

    const newConversationIds = Object.keys(currentFolderStatusMap).filter(
      (convId) =>
        currentFolderStatusMap[convId] &&
        prevFolderStatusMap[convId] !== currentFolderStatusMap[convId]
    );

    // Check if conversations are already in store (from socket upsert)
    const newConversationsInStore = newConversationIds.filter((convId) =>
      currentConversations.some((conv) => {
        const conversationId = conv.lastMessage?.conversationId || conv.id;
        return conversationId === convId && conv.fanpageName === selectedFanpageName;
      })
    );

    // If all new conversations are already in store from socket, skip API fetch
    // This prevents unnecessary API calls when socket events update the conversation list
    if (newConversationIds.length > 0 && newConversationIds.length === newConversationsInStore.length) {
      prevFolderStatusMapRef.current = currentFolderStatusMapKey;
      return;
    }

    // Only refresh if:
    // 1. refreshTrigger was explicitly called (user action like mark as done from UI)
    // 2. OR if there are new conversation IDs that are NOT in store (need to fetch from API)
    const shouldRefresh = hasFolderStatusChange && selectedFanpageName;
    const isRelevantTab = ConversationTabHelpers.requiresFolderStatus(activeTab);
    const hasExplicitRefresh = refreshTrigger > prevRefreshTriggerRef.current;
    
    // Only force refresh if:
    // - Explicit refresh was triggered (user action)
    // - OR there are new conversations that are NOT in store (need to fetch)
    const shouldForceRefresh = (hasExplicitRefresh && isRelevantTab) || 
      (shouldRefresh && isRelevantTab && newConversationIds.length > 0 && newConversationIds.length !== newConversationsInStore.length);
    
    if (shouldForceRefresh || (shouldRefresh && !debouncedSearchValue && hasExplicitRefresh)) {
      isFetchingFromFolderStatusChangeRef.current = true;
      
      const selectedPage = facebookPages.find(
        (page) => page.name === selectedFanpageName
      );

      if (selectedPage) {
        const fetchConversations = async () => {
          try {
            let folder: string | undefined;
            let excludeFolders: string[] | undefined;

            if (!ConversationTabHelpers.requiresFolderStatus(activeTab)) {
              excludeFolders = [];
            } else {
              folder = ConversationTabHelpers.getFolderForTab(activeTab);
            }


            const result = await getFacebookConversationsWithMeta({
              pageId: selectedPage.id,
              offset: 0,
              limit: 50,
              tagIds: selectedTagIds.length > 0 ? selectedTagIds : undefined,
              search: debouncedSearchValue.trim() || undefined,
              folder,
              excludeFolders,
            });

            const conversationsWithFanpage = result.conversations.map((conv) => ({
              ...conv,
              fanpageName: selectedFanpageName,
            }));

            const { conversations: currentConversations } = useChatStore.getState();
            const conversationsFromOtherFanpages = currentConversations.filter(
              (conv) =>
                !(
                  conv.fanpageName === selectedFanpageName &&
                  conv.channel === CHANNELS.FACEBOOK
                )
            );

            const allConversations = [
              ...conversationsWithFanpage,
              ...conversationsFromOtherFanpages,
            ];

            if (!isResettingPageRef.current) {
              const expectedSearchTerm = debouncedSearchValue.trim() || "";
              const currentState = useChatStore.getState();
              if (
                currentState.selectedChannel !== CHANNELS.FACEBOOK ||
                currentState.selectedFanpageName !== selectedFanpageName
              ) {
                return;
              }

              setConversationsWithSearch(allConversations, expectedSearchTerm);

              updatePagination(selectedPage.id, {
                offset: result.offset,
                total: result.total,
                limit: result.limit,
                hasMore:
                  result.offset + result.conversations.length < result.total,
              });
              // Note: Auto-select conversation is now handled by a separate useEffect
              // to ensure conversations are fully loaded before selecting
            }
          } catch (error) {
            console.error("Error refreshing conversations after folder status change:", error);
          } finally {
            isFetchingFromFolderStatusChangeRef.current = false;
          }
        };

        fetchConversations();
      }
    }

    prevFolderStatusMapRef.current = currentFolderStatusMapKey;
  }, [
    storeFolderStatusMap,
    selectedFanpageName,
    facebookPages,
    debouncedSearchValue,
    activeTab,
    selectedTagIds,
    setConversationsWithSearch,
    isResettingPageRef,
    isFetchingFromFolderStatusChangeRef,
    updatePagination,
  ]);
  // Store the latest refresh trigger value when it increases (used to trigger refresh logic elsewhere)
  useEffect(() => {
    if (refreshTrigger > prevRefreshTriggerRef.current) {
      prevRefreshTriggerRef.current = refreshTrigger;
    }
  }, [refreshTrigger, activeTab, selectedFanpageName]);
};
